import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Compass, X } from "lucide-react";
import { trackEvent } from "../../lib/analytics";

/* Floating "Plan my journey" pill shown site-wide: scrolls to the page's
   enquiry form (#home-enquiry / #page-enquiry) when present, otherwise
   navigates to /contact. Hides while a form is in view, after any input/submit
   inside it, or when dismissed (per tab). */
const HIDDEN_PATHS = [/^\/admin/, /^\/account/, /^\/partner/, /^\/sign-in/, /^\/guide/];

export default function StickyEnquireNudge() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [visible, setVisible] = useState(false);
    const [lift, setLift] = useState(0);
    const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("enquire_nudge_off") === "1");
    const engagedRef = useRef(false);

    const hiddenHere = HIDDEN_PATHS.some((re) => re.test(pathname));

    useEffect(() => {
        engagedRef.current = false;
    }, [pathname]);

    useEffect(() => {
        if (dismissed || hiddenHere) return undefined;
        const section = () => document.getElementById("home-enquiry") || document.getElementById("page-enquiry");

        const onInteract = (e) => {
            const sec = section();
            if (sec && sec.contains(e.target)) {
                engagedRef.current = true;
                if (e.type === "submit") sessionStorage.setItem("enquire_nudge_off", "1");
                setVisible(false);
            }
        };
        const onScroll = () => {
            if (engagedRef.current) return setVisible(false);
            const sec = section();
            if (sec) {
                const r = sec.getBoundingClientRect();
                const inView = r.top < window.innerHeight && r.bottom > 0;
                setVisible(!inView && window.scrollY > 700);
            } else {
                setVisible(window.scrollY > 700);
            }
            const banner = document.querySelector("[data-testid='cookie-banner']");
            setLift(banner ? banner.offsetHeight : 0);
        };
        document.addEventListener("input", onInteract, true);
        document.addEventListener("submit", onInteract, true);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => {
            document.removeEventListener("input", onInteract, true);
            document.removeEventListener("submit", onInteract, true);
            window.removeEventListener("scroll", onScroll);
        };
    }, [dismissed, hiddenHere, pathname]);

    const goToForm = () => {
        setVisible(false);
        trackEvent("nudge_cta_clicked", { path: pathname });
        const sec = document.getElementById("home-enquiry") || document.getElementById("page-enquiry");
        if (sec) sec.scrollIntoView({ behavior: "smooth", block: "start" });
        else navigate("/contact");
    };
    const dismiss = () => {
        sessionStorage.setItem("enquire_nudge_off", "1");
        setDismissed(true);
    };

    if (dismissed || hiddenHere) return null;

    return (
        <div
            aria-hidden={!visible}
            data-testid="sticky-enquire-nudge"
            style={{ marginBottom: lift }}
            className={`fixed z-[60] left-1/2 -translate-x-1/2 bottom-20 sm:bottom-6 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"}`}
        >
            <div className="flex items-center gap-1 pl-5 pr-2 py-2 rounded-full bg-primary/95 backdrop-blur text-primary-foreground shadow-2xl border border-primary-foreground/15">
                <span className="hidden sm:inline text-xs text-primary-foreground/70 mr-2">Still dreaming?</span>
                <button
                    type="button"
                    onClick={goToForm}
                    data-testid="sticky-enquire-nudge-cta"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-accent-foreground text-[10px] tracking-[0.25em] uppercase font-semibold hover:brightness-110 transition"
                >
                    <Compass size={13} strokeWidth={1.75} /> Plan my journey
                </button>
                <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={dismiss}
                    data-testid="sticky-enquire-nudge-dismiss"
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                >
                    <X size={14} strokeWidth={1.75} />
                </button>
            </div>
        </div>
    );
}
